import React from 'react';
import MainLayout from 'components/layout/MainLayout';
import BlogArticle from 'components/blog/BlogArticle';
import { ASSET_PREFIX } from 'utils/constants';
import { NextSeo } from 'next-seo';
import RoundedButton from 'components/RoundedButton';
import Separator from 'components/Separator';
import BlogLink from 'components/blog/BlogLink';
import { getCta } from 'utils/common';

const Article = () => {
  const IMAGE_DIRECTORY = `${ASSET_PREFIX}/images/blog/articles/road-trip-wonders`;

  return (
    <MainLayout additionalClassName="blogPost">
      <NextSeo
        title="Road Trip Wonders: Must-See Routes and Itineraries | DAYMADE"
        description="Are you ready for an epic adventure on the open road? Embark on a road trip and explore some of the most breathtaking destinations around the world. From the iconic Route 66 in California to the stunning landscapes of Glencoe and the Highlands, these road trip routes will leave you in awe."
        openGraph={{
          title: 'Road Trip Wonders: Must-See Routes and Itineraries | DAYMADE',
          description:
            'Are you ready for an epic adventure on the open road? Embark on a road trip and explore some of the most breathtaking destinations around the world. From the iconic Route 66 in California to the stunning landscapes of Glencoe and the Highlands, these road trip routes will leave you in awe.',
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/road-trip-wonders',
          images: [
            {
              url: `${IMAGE_DIRECTORY}/hero.jpg`,
              alt: 'Route 66',
            },
          ],
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'road trip,travel,daymade',
        // }]}
      />

      <BlogArticle
        imageDirectory={IMAGE_DIRECTORY}
        heroAltText="Route 66"
        heroImageYPosition="0%"
      >
        <>
          <h1 style={{ fontSize: '1.6em', lineHeight: '1.4em' }}>
            Road Trip Wonders: Must-See Routes and Itineraries
          </h1>

          <p className="subheading">
            Are you ready for an epic adventure on the open road?
          </p>

          <div
            className="separator"
            style={{ width: '80%', marginTop: 0 }}
          />

          <p>
            Are you ready for an epic adventure on the open road? Embark on a
            road trip and explore some of the most breathtaking destinations
            around the world. From the iconic Route 66 in California to the
            stunning landscapes of the Highlands, these road trip routes will
            leave you in awe. Whether it’s in a{' '}
            <BlogLink internal="/prizes/details/win-camper-van">
              camper van
            </BlogLink>{' '}
            or a{' '}
            <BlogLink internal="/prizes/details/win-a-tesla">Tesla</BlogLink>,
            get your engines revving and let's hit the road!
          </p>

          <h2>California & Route 66: Where Dreams Come True</h2>

          <p>
            Start your road trip in the Golden State of{' '}
            <BlogLink internal="/prizes/details/california">
              California
            </BlogLink>
            , where endless possibilities await. Begin your journey in Los
            Angeles, the entertainment capital of the world. Cruise along the
            iconic Pacific Coast Highway, with its stunning coastal views and
            charming beach towns like Santa Monica and Malibu.
          </p>

          <p>
            Next, head inland and embark on the legendary Route 66, the ultimate
            road trip route. Drive through the Mojave Desert, where you'll
            encounter quirky roadside attractions and nostalgic Americana. Make
            a stop at the Grand Canyon National Park and witness the
            awe-inspiring beauty of this natural wonder. For areas less
            well-trodden, grasp the allure of the{' '}
            <BlogLink internal="/prizes/details/7-nights-in-santa-ynez-valley">
              Santa Ynez Valley
            </BlogLink>
            , a rugged swathe of Santa Barbara County that’s home to one of
            California’s most intriguing wine regions.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary1.jpg`}
            alt="Puglia"
            className="singleBlogImage"
          />

          <h2>Puglia: The Hidden Gem of Italy</h2>

          <p>
            Venture off the beaten path and explore the enchanting region of{' '}
            <BlogLink internal="/prizes/details/puglia">Puglia</BlogLink> in
            southern Italy. Sometimes overshadowed by the rolling hills of
            Tuscany or the picturesque shores of the Amalfi Coast, the region of
            Puglia often sits lower on Italy bucket lists.
          </p>

          <p>
            In reality though, this region has it all: quaint towns, verdant
            countryside, crystal-clear seas and the freshest food. Known for its
            whitewashed trulli houses and picturesque coastal towns, Puglia is a
            road tripper's paradise. Start your journey in the charming city of
            Bari and make your way to Alberobello, a UNESCO World Heritage site
            famous for its unique trulli architecture.
          </p>

          <p>
            Venture back towards the coastline and discover the stunning beaches
            of Polignano a Mare and Otranto. Be sure to indulge in the region's
            delicious cuisine, including fresh seafood and locally produced
            olive oil. Further south lies Ostuni, with its stunning whitewashed
            old town. Immerse yourself in the rich history and culture of Puglia
            as you visit ancient ruins and historic towns like Lecce with its
            Baroque architecture, and further west Gallipoli, a charming coastal
            town rich in character. On the way back to Bari, try to squeeze in
            Matera, an ancient city on a rocky outcrop with a complex of cave
            dwellings carved into the mountainside, some of which have been
            converted into hotels.
          </p>

          <h2>South of France: Riviera Bliss</h2>

          <p>
            Experience the glitz and glamour of the French Riviera on a road
            trip through the{' '}
            <BlogLink internal="/prizes/details/win-a-holiday-to-south-of-france">
              South of France
            </BlogLink>
            . Start in Nice, where you can stroll along the Promenade des
            Anglais and soak up the Mediterranean sunshine. Explore the charming
            hilltop villages of Eze and Saint-Paul-de-Vence, known for their
            stunning views and artistic heritage.
          </p>

          <p>
            Continue your journey to the iconic city of Cannes, famous for its
            film festival and luxurious resorts. Drive along the coastal roads
            and discover the stunning beaches of Antibes and Juan-les-Pins. End
            your road trip in the glamorous city of Monaco, where you can try
            your luck at the famous Monte Carlo Casino.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary2.jpg`}
            alt="Glencoe and the Highlands"
            className="singleBlogImage"
          />

          <h2>Glencoe and the Highlands: Scotland's Natural Beauty</h2>

          <p>
            Prepare to be mesmerised by the rugged landscapes of{' '}
            <BlogLink internal="/prizes/details/win-roadtrip-scotland">
              Glencoe and the Highlands
            </BlogLink>{' '}
            in Scotland. Start your journey in Edinburgh and head north into the
            heart of the Scottish Highlands. Drive through the majestic Glencoe
            Valley, surrounded by towering mountains and cascading waterfalls.
          </p>

          <p>
            Explore the mystical Isle of Skye, with its dramatic cliffs and
            fairy-tale landscapes. Marvel at the iconic Eilean Donan Castle,
            perched on a tiny island at the meeting point of three lochs.
            Immerse yourself in Scottish history and culture as you visit
            historic castles and charming Highland village, of which there’s a
            whole plethora.
          </p>

          <p>
            Some of our favourites include Killin, sitting at one end of Loch
            Tay, which is pretty small so ideal for a pit stop; Torridon, which
            sits at the foot of majestic Liathach & being the largest village in
            the area means it’s got a few little pubs and lovely spots to wander
            and Applecross, the big draw here being the breathtaking Bealach na
            Ba Road which is one of the best drives in the UK, perfect if you’re
            heading from Applecross to Ardarroch.
          </p>

          <h2>Lake District: England's Green Paradise</h2>

          <p>
            Discover the natural beauty of the{' '}
            <BlogLink internal="/prizes/details/win-a-lake-district-staycation">
              Lake District
            </BlogLink>{' '}
            in northwest England on a road trip through this picturesque region.
            Begin your journey in the charming town of Keswick and explore the
            stunning landscapes of Derwentwater and Borrowdale Valley. Hike up
            the iconic Cat Bells for panoramic views of the surrounding
            mountains and lakes.
          </p>

          <p>
            Continue your road trip to Windermere, the largest lake in England.
            Take a leisurely cruise on the lake or explore the quaint villages
            of Ambleside and Bowness-on-Windermere. Don't forget to sample the
            region's famous Kendal mint cake, a sweet treat for your road trip
            adventures.
          </p>

          <Separator style={{ margin: '1em 0 2.5em' }} />

          <p>
            So fuel up your car, pack your bags, and hit the road for the
            adventure of a lifetime. These must-see routes and itineraries are
            waiting to be explored. Happy travels! Remember, the journey is just
            as important as the destination. Take your time, embrace the
            unexpected, and let the road guide you to incredible experiences
            along the way.
          </p>

          <RoundedButton
            href={getCta()?.url}
            style={{ minWidth: '180px', margin: '1em 0 1.5em' }}
          >
            Enter Now
          </RoundedButton>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default Article;
